import type { TableStateControl } from '../../hooks';
import { IconButton } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { SearchButtonInput } from '../SearchButtonInput';
import { SkeletonSpinner } from '../SkeletonLoader';
import { Text } from '../Text';
import { getPageRange } from './pageRange';

export type LayerLibraryToolbarProps = {
  /** Client-side table state (from `useTableStoreState`). */
  stateControl: TableStateControl;
  /** Total rows after search filtering — drives the range label and page count. */
  totalItems: number;
  isLoading?: boolean;
  className?: string;
};

/**
 * Slim toolbar for the Layer Library table: global search + pagination only.
 * No filter / column / density / export controls (out of scope for the catalog table).
 */
function LayerLibraryToolbar({ stateControl, totalItems, isLoading = false, className }: LayerLibraryToolbarProps) {
  const { pagination, currentPage, setCurrentPage, globalFilter, setGlobalFilter } = stateControl;

  // Clamped display math: `currentPage` can be stale when the filter shrinks `totalItems`.
  const { start, end, totalPages, clampedPage } = getPageRange(currentPage, pagination.pageSize, totalItems);

  return (
    <div className={`flex h-8 items-center justify-between ${className ?? ''}`}>
      <SearchButtonInput
        keepExpanded={true}
        value={globalFilter}
        onChange={setGlobalFilter}
        className="h-6"
        data-testid="layer-library-search"
      />
      <div className="flex items-center gap-2">
        <Text className="text-2xs text-primary">
          {isLoading ? <SkeletonSpinner size={14} /> : `${start}-${end} of ${totalItems.toLocaleString()}`}
        </Text>
        <ButtonGroup>
          <IconButton
            icon="chevronLeft"
            variant="secondary"
            size="icon"
            disabled={clampedPage <= 1}
            onClick={() => setCurrentPage(clampedPage - 1)}
            aria-label="Previous page"
            data-testid="layer-library-page-prev"
          />
          <IconButton
            icon="chevronRight"
            variant="secondary"
            size="icon"
            disabled={clampedPage >= totalPages}
            onClick={() => setCurrentPage(clampedPage + 1)}
            aria-label="Next page"
            data-testid="layer-library-page-next"
          />
        </ButtonGroup>
      </div>
    </div>
  );
}

export { LayerLibraryToolbar };
